import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { JSONRecordData } from '../Log/graph.util';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import { I_S3Credentials } from '../../../interfaces/S3Types';
import routes from '../../../Resources/routes.json';
import CircularProgress from '@mui/material/CircularProgress';
import { I_Installation } from 'src/interfaces/InstallationTypes';
import MainStatsSodioHome from './MainStatsSodioHome';
import {
  ActiveCluster,
  getActiveClusters
} from '../Information/installationSetupUtils';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import axiosConfig from 'src/Resources/axiosConfig';
import { BatteryAnomalyLogEntry } from 'src/interfaces/BatteryAnomalyTypes';

interface BatteryViewSodioHomeProps {
  values: JSONRecordData;
  s3Credentials: I_S3Credentials;
  installationId: number;
  installation: I_Installation;
  connected: boolean;
}

function BatteryViewSodioHome(props: BatteryViewSodioHomeProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }

  const currentLocation = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const inverter = (props.values as any)?.InverterRecord;
  const batteryClusterNumber = props.installation.batteryClusterNumber;
  const batterySerialNumbers = props.installation.batterySerialNumbers;

  const hasDevices = !!inverter?.Devices;

  const { currentUser } = useContext(UserContext);
  const showAnomalyLog = currentUser?.userType === UserType.admin;
  const [anomalyLog, setAnomalyLog] = useState<BatteryAnomalyLogEntry[]>([]);

  useEffect(() => {
    if (!showAnomalyLog || !props.installationId) return;
    axiosConfig
      .get('/GetBatteryVoltageAnomalyLog', {
        params: { installationId: props.installationId }
      })
      .then((res) => {
        if (Array.isArray(res.data)) setAnomalyLog(res.data);
      })
      .catch(() => setAnomalyLog([]));
  }, [showAnomalyLog, props.installationId]);

  const activeClusters: ActiveCluster[] = useMemo(() => {
    const parsed = getActiveClusters(batterySerialNumbers || '');
    if (parsed.length > 0) return parsed;
    // Legacy/empty fallback: assume 2 clusters per inverter (all current Sinexcel
    // presets), which matches the previous floor(i/2)+1 / (i%2)+1 mapping.
    // For Growatt (batteryClusterNumber = 1) this collapses to a single row.
    return Array.from({ length: batteryClusterNumber }, (_, i) => ({
      invIdx: Math.floor(i / 2),
      clIdx: i % 2,
      flatIdx: i
    }));
  }, [batterySerialNumbers, batteryClusterNumber]);

  const inverterCount = activeClusters.reduce(
    (max, c) => Math.max(max, c.invIdx + 1),
    0
  );
  const showInverterLabel = hasDevices && inverterCount > 1;

  const sortedBatteryView = inverter
    ? activeClusters.map(({ invIdx, clIdx, flatIdx }) => {
        const label = showInverterLabel
          ? intl.formatMessage(
              {
                id: 'batteryClusterInInverter',
                defaultMessage: 'Battery Cluster {cl} in Inverter {inv}'
              },
              { cl: clIdx + 1, inv: invIdx + 1 }
            )
          : intl.formatMessage(
              { id: 'batteryClusterN', defaultMessage: 'Battery Cluster {n}' },
              { n: clIdx + 1 }
            );

        if (hasDevices) {
          // Sinexcel: Devices keyed by "1","2",... (1-based dict keys)
          const device = inverter.Devices[String(invIdx + 1)];
          const bi = clIdx + 1;
          return {
            BatteryId: String(flatIdx + 1),
            label,
            battery: {
              Voltage: device?.[`Battery${bi}PackTotalVoltage`] ?? 0,
              Current: device?.[`Battery${bi}PackTotalCurrent`] ?? 0,
              Power: device?.[`Battery${bi}Power`] ?? 0,
              Soc:
                device?.[`Battery${bi}Soc`] ??
                device?.[`Battery${bi}SocSecondvalue`] ??
                0
            }
          };
        }
        // Growatt: flat Battery1, Battery2, ... on InverterRecord
        const index = clIdx + 1;
        return {
          BatteryId: String(flatIdx + 1),
          label,
          battery: {
            Voltage: inverter[`Battery${index}Voltage`] ?? 0,
            Current: inverter[`Battery${index}Current`] ?? 0,
            Power: inverter[`Battery${index}Power`] ?? 0,
            Soc: inverter[`Battery${index}Soc`] ?? 0
          }
        };
      })
    : [];

  const [loading, setLoading] = useState(sortedBatteryView.length == 0);
  const handleMainStatsButton = () => {
    navigate(routes.mainstats);
  };

  useEffect(() => {
    if (sortedBatteryView.length == 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [sortedBatteryView]);

  return (
    <>
      {!props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            <FormattedMessage id="unableToCommunicate" defaultMessage="Unable to communicate with the installation" />
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
          </Typography>
        </Container>
      )}
      {loading && props.connected && (
        <Container
          maxWidth="xl"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '70vh'
          }}
        >
          <CircularProgress size={60} style={{ color: '#ffc04d' }} />
          <Typography
            variant="body2"
            style={{ color: 'black', fontWeight: 'bold' }}
            mt={2}
          >
            <FormattedMessage id="batteryServiceNotAvailable" defaultMessage="Battery service is not available at the moment" />
          </Typography>
          <Typography variant="body2" style={{ color: 'black' }}>
            <FormattedMessage id="pleaseWaitOrRefresh" defaultMessage="Please wait or refresh the page" />
          </Typography>
        </Container>
      )}

      {!loading && props.connected && (
        <Container maxWidth="xl">
          <Grid container>
            <Grid
              item
              xs={6}
              md={6}
              sx={{
                display:
                  !currentLocation.pathname.includes('detailed_view') &&
                  !currentLocation.pathname.includes('mainstats')
                    ? 'block'
                    : 'none'
              }}
            >
              <Button
                variant="contained"
                sx={{
                  marginTop: '20px',
                  backgroundColor: '#808080',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                <FormattedMessage
                  id="battery_view"
                  defaultMessage="Battery View"
                />
              </Button>

              <Button
                variant="contained"
                onClick={handleMainStatsButton}
                sx={{
                  marginTop: '20px',
                  marginLeft: '20px',
                  backgroundColor: '#ffc04d',
                  color: '#000000',
                  '&:hover': { bgcolor: '#f7b34d' }
                }}
              >
                <FormattedMessage id="main_stats" defaultMessage="Main Stats" />
              </Button>
            </Grid>
          </Grid>

          <Grid container>
            <Routes>
              <Route
                path={routes.mainstats + '/*'}
                element={
                  <MainStatsSodioHome
                    s3Credentials={props.s3Credentials}
                    id={props.installationId}
                    batteryClusterNumber={props.installation.batteryClusterNumber}
                    activeClusters={activeClusters}
                    showInverterLabel={showInverterLabel}
                  ></MainStatsSodioHome>
                }
              />
            </Routes>
          </Grid>

          <TableContainer
            component={Paper}
            sx={{
              marginTop: '20px',
              marginBottom: '20px',
              display:
                !currentLocation.pathname.includes('detailed_view') &&
                !currentLocation.pathname.includes('mainstats')
                  ? 'block'
                  : 'none'
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="center"><FormattedMessage id="battery" defaultMessage="Battery" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="power" defaultMessage="Power" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="batteryVoltage" defaultMessage="Battery Voltage" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="current" defaultMessage="Current" /></TableCell>
                  <TableCell align="center"><FormattedMessage id="soc" defaultMessage="SoC" /></TableCell>
                  {/*<TableCell align="center"><FormattedMessage id="soh" defaultMessage="SoH" /></TableCell>*/}
                  {/*<TableCell align="center">Daily Charge Energy</TableCell>*/}
                  {/*<TableCell align="center">Daily Discharge Energy</TableCell>*/}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBatteryView.map(({ BatteryId, label, battery }) => (
                  <TableRow
                    key={BatteryId}
                    style={{
                      height: '10px'
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="center"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {/* Detailed battery view commented out */}
                      {/*<Link
                        style={{ color: 'black' }}
                        to={routes.detailed_view + BatteryId}
                      >*/}
                        {label}
                      {/*</Link>*/}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '13%',
                        textAlign: 'center'
                      }}
                    >
                      {battery.Power + ' ' + 'W'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '13%',
                        textAlign: 'center',
                        backgroundColor:
                          battery.Voltage < 32 || battery.Voltage > 63
                            ? '#FF033E'
                            : '#32CD32',
                        color: 'inherit'
                      }}
                    >
                      {battery.Voltage + ' V'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '13%',
                        textAlign: 'center',
                        backgroundColor: '#32CD32',
                        color: 'inherit'
                      }}
                    >
                      {battery.Current + ' A'}
                    </TableCell>
                    <TableCell
                      sx={{
                        width: '8%',
                        textAlign: 'center',
                        backgroundColor:
                          battery.Soc > 20
                            ? '#32CD32'
                            : battery.Soc >= 10
                              ? '#ffbf00'
                              : '#FF033E',
                        color: 'inherit'
                      }}
                    >
                      {battery.Soc + ' %'}
                    </TableCell>
                    {/*<TableCell
                      sx={{
                        width: '8%',
                        textAlign: 'center',
                        backgroundColor:
                          battery.Soh ==100
                            ? '#32CD32'
                            : battery.Soc >= 90
                              ? '#ffbf00'
                              : '#FF033E',
                        color: 'inherit'
                      }}
                    >
                      {battery.Soh + ' %'}
                    </TableCell>*/}
                    {/*<TableCell*/}
                    {/*  sx={{*/}
                    {/*    width: '15%',*/}
                    {/*    textAlign: 'center',*/}
                    {/*    backgroundColor: '#32CD32',*/}
                    {/*    color: 'inherit'*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  {battery.DailyChargeEnergy + ' Wh'}*/}
                    {/*</TableCell>*/}
                    {/*<TableCell*/}
                    {/*  sx={{*/}
                    {/*    width: '15%',*/}
                    {/*    textAlign: 'center',*/}
                    {/*    backgroundColor: '#32CD32',*/}
                    {/*    color: 'inherit'*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  {battery.DailyDischargeEnergy + ' Wh'}*/}
                    {/*</TableCell>*/}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      )}
      {showAnomalyLog && (
        <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
          <Card>
            <Typography
              variant="h6"
              sx={{ padding: '12px 16px', fontWeight: 'bold' }}
            >
              <FormattedMessage
                id="voltageAnomalyLogTitle"
                defaultMessage="Full-charge low-voltage log"
              />
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <FormattedMessage id="battery" defaultMessage="Battery" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage
                        id="voltageAnomalyTime"
                        defaultMessage="Time"
                      />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="voltage" defaultMessage="Voltage" />
                    </TableCell>
                    <TableCell>
                      <FormattedMessage id="soc" defaultMessage="SOC" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {anomalyLog.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          <FormattedMessage
                            id="noVoltageAnomalies"
                            defaultMessage="No anomalies recorded"
                          />
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    anomalyLog.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>{entry.batteryLabel}</TableCell>
                        <TableCell>{entry.detectedAt}</TableCell>
                        <TableCell>{entry.voltage.toFixed(2)} V</TableCell>
                        <TableCell>{entry.soc.toFixed(0)} %</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Container>
      )}
    </>
  );
}

export default BatteryViewSodioHome;
